'use client';

import Link from 'next/link';
import Image from 'next/image';
import Cookies from 'js-cookie';
import classes from './StaticHeader.module.scss';
import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Drawer, Popover } from '@mantine/core';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import HeaderEditorModal from '@/src/components/HeaderMenu/HeaderEditorModal';

type HeaderLink = { label: string; href: string; description?: string };


type HeaderColumn = {
  title: string;
  links: HeaderLink[];
};

type HeaderNavItem = {
  label: string;
  href?: string;
  columns?: HeaderColumn[];
};

type SurheaderItem = {
  icon?: string;
  href: string;
  label: string;
};

export type StaticHeaderSlice = {
  type: 'header';
  logoSrc: string;
  logoAlt: string;

  meta?: SurheaderItem[];

  nav: HeaderNavItem[];
  cta?: { label: string; href: string };

  auth: {
    dashboardLabel: string;
    dashboardHref: string;
    loginLabel: string;
    loginHref: string;
    logoutLabel: string;
    afterLogoutHref?: string;
  };

  supportedLanguage?: {
    fr: { label: string; img: string };
    en: { label: string; img: string };
  };
  selectedLanguage?: 'fr' | 'en';

  important_message?: {
    text: string;
    color?: string;
    textColor?: string;
  };
};

const LOCALES = ['fr', 'en'] as const;
type Locale = (typeof LOCALES)[number];

function getLocaleFromPath(pathname: string): Locale | null {
  const first = pathname.split('/')[1];
  return (LOCALES as readonly string[]).includes(first) ? (first as Locale) : null;
}

function withLocalePrefix(pathname: string, nextLocale: Locale): string {
  const parts = pathname.split('/');
  const first = parts[1];

  if ((LOCALES as readonly string[]).includes(first)) {
    parts[1] = nextLocale;
    return parts.join('/') || `/${nextLocale}`;
  }

  return `/${nextLocale}${pathname.startsWith('/') ? '' : '/'}${pathname}`;
}

function LogoText() {
  return (
    <>
      <span>Ateliers</span>
      <span>Mini Monkeys</span>
    </>
  );
}

export function StaticHeader({ data }: { data: StaticHeaderSlice }) {
  const pathname = usePathname();
  const router = useRouter();
  const dictionary = useDictionary();

  const [canEditHeader, setCanEditHeader] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openedDesktopMenu, setOpenedDesktopMenu] = useState<string | null>(null);
  const [isAtTop, setIsAtTop] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch('/api/validate-token', {
          method: 'GET',
          credentials: 'include',
        });

        if (!res.ok) {
          setCanEditHeader(false);
          setIsLoggedIn(false);
          return;
        }

        const data = await res.json();
        const role = data?.user?.role ?? null;
        const valid = !!data?.valid;

        setIsLoggedIn(valid);

        if (valid && (role === 'superAdmin' || role === 'admin')) {
          setCanEditHeader(true);
        } else {
          setCanEditHeader(false);
        }
      } catch (err) {
        console.error('Auth validation failed', err);
        setCanEditHeader(false);
        setIsLoggedIn(false);
      }
    };

    checkAuth();
  }, [pathname]);

  useEffect(() => {
    const THRESHOLD = 8;
    const onScroll = () => setIsAtTop(window.scrollY <= THRESHOLD);

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpenedDesktopMenu(null);
  }, [pathname]);

  const handleLogout = async () => {
    await fetch('/api/logout', {
      method: 'POST',
      credentials: 'include',
    });

    setIsLoggedIn(false);
    setCanEditHeader(false);
    router.push('/login');
    router.refresh();
  };

  const currentLocale: Locale = useMemo(() => {
    const cookieLocale = Cookies.get('NEXT_LOCALE') as Locale | undefined;
    if (cookieLocale === 'fr' || cookieLocale === 'en') return cookieLocale;

    const pathLocale = getLocaleFromPath(pathname);
    if (pathLocale) return pathLocale;

    if (data.selectedLanguage === 'fr' || data.selectedLanguage === 'en') {
      return data.selectedLanguage;
    }

    return 'fr';
  }, [pathname, data.selectedLanguage]);

  const changeLanguage = (nextLocale: Locale) => {
    if (nextLocale === currentLocale) return;

    Cookies.set('NEXT_LOCALE', nextLocale, { expires: 365, path: '/' });

    const nextPath = withLocalePrefix(pathname, nextLocale);
    router.push(nextPath);
    router.refresh();
  };

  const DEFAULT_LANG_META = {
    fr: { label: 'Français', img: 'https://flagcdn.com/w20/fr.png' },
    en: { label: 'English', img: 'https://flagcdn.com/w20/gb.png' },
  } as const;

  const supportedLanguage = data.supportedLanguage ?? DEFAULT_LANG_META;

  const metaItems = data.meta ?? [];
  const hasMeta = metaItems.length > 0;

  const importantText = data.important_message?.text?.trim();
  const hasImportant = !!importantText;

  const showBanner = hasImportant && isAtTop && !drawerOpen;

  const bannerStyle: CSSProperties | undefined = hasImportant
    ? ({
      ['--imp-bg' as any]:
        data.important_message?.color ?? 'rgba(255, 205, 73, 0.95)',
      ['--imp-text' as any]:
        data.important_message?.textColor ?? 'rgba(20, 24, 28, 0.95)',
    } as CSSProperties)
    : undefined;

  return (
    <>
      <header className={classes.header} role="banner">
        {canEditHeader && (
          <HeaderEditorModal
            mainHeader={JSON.parse(JSON.stringify(data.nav ?? [])) as any}
            metaHeader={JSON.parse(JSON.stringify(data.meta ?? [])) as any}
            navItems={JSON.parse(JSON.stringify(data.nav ?? []))}
            metaItems={JSON.parse(JSON.stringify(data.meta ?? []))}
            cta={data.cta ? JSON.parse(JSON.stringify(data.cta)) : undefined}
            auth={data.auth ? JSON.parse(JSON.stringify(data.auth)) : undefined}
            importantMessage={
              data.important_message
                ? JSON.parse(JSON.stringify(data.important_message))
                : undefined
            }
          />
        )}

        <div className={classes.mobileBar} aria-label="Mobile top bar">
          <Link
            href="/"
            className={classes.mobileLogo}
            aria-label="Home"
            onClick={() => setDrawerOpen(false)}
          >
            <Image
              src={data.logoSrc}
              alt={dictionary.compagnyName}
              width={160}
              height={70}
              priority
              unoptimized
              sizes="160px"
              quality={45}
            />
          </Link>

          <button
            type="button"
            className={classes.burger}
            aria-label="Open menu"
            onClick={() => setDrawerOpen(true)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>

        <div className={classes.fullWrap}>
          <div className={classes.fullWrapInner}>
            {hasMeta && (
              <div className={classes.surheader} role="navigation" aria-label="Top">
                <div className={classes.surheaderInner}>
                  {metaItems.map((item) => (
                    <Link
                      key={`${item.href}-${item.label}`}
                      href={item.href}
                      className={classes.surItem}
                    >
                      {item.icon && (
                        <img
                          src={item.icon}
                          alt=""
                          aria-hidden="true"
                          className={classes.surIcon}
                          width={18}
                          height={18}
                        />
                      )}
                      <span className={classes.surLabel}>{item.label}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className={classes.inner}>
              <div>
                <Link href="/" className={classes.logoBlock} aria-label="Home">
                  <Image
                    src={data.logoSrc}
                    alt={dictionary.compagnyName}
                    width={150}
                    height={72}
                    priority
                    unoptimized
                    sizes="150px"
                    quality={45}
                    className={classes.logoImg}
                  />
                </Link>
              </div>

              <nav className={classes.nav} aria-label="Primary">
                <ul className={classes.navList}>
                  {data.nav.map((item) => {
                    const hasDropdown = item.columns && item.columns.length > 0;

                    if (!hasDropdown) {
                      return (
                        <li key={item.label} className={classes.navItem}>
                          <Link className={classes.navLink} href={item.href ?? '#'}>
                            {item.label}
                          </Link>
                        </li>
                      );
                    }

                    const isOpened = openedDesktopMenu === item.label;

                    return (
                      <li
                        key={item.label}
                        className={`${classes.navItem} ${classes.hasDropdown}`}
                      >
                        <Popover
                          opened={isOpened}
                          onChange={(opened) => {
                            setOpenedDesktopMenu(opened ? item.label : null);
                          }}
                          position="bottom"
                          offset={12}
                          width={640}
                          shadow="none"
                          radius="md"
                          withArrow={false}
                          trapFocus={false}
                          closeOnClickOutside
                          closeOnEscape
                          withinPortal
                        >
                          <Popover.Target>
                            <button
                              type="button"
                              className={`${classes.navButton} ${isOpened ? classes.navButtonActive : ''
                                }`}
                              aria-haspopup="menu"
                              aria-expanded={isOpened}
                              onClick={() => {
                                setOpenedDesktopMenu((prev) =>
                                  prev === item.label ? null : item.label,
                                );
                              }}
                            >
                              {item.label}
                              <span className={classes.chevron} aria-hidden="true">
                                ▾
                              </span>
                            </button>
                          </Popover.Target>

                          <Popover.Dropdown className={classes.popoverDropdown}>
                            <div className={classes.mega} role="menu" aria-label={item.label}>
                              <div
                                className={classes.megaInner}
                                style={{
                                  gridTemplateColumns: `repeat(${Math.min(
                                    item.columns!.length,
                                    2,
                                  )}, minmax(0, 1fr))`,
                                }}
                              >
                                {item.columns!.map((col) => (
                                  <div key={col.title} className={classes.col}>
                                    <p className={classes.colTitle}>{col.title}</p>
                                    <ul className={classes.colList}>
                                      {col.links.map((l) => (
                                        <li key={l.href} className={classes.colItem}>
                                          <Link
                                            href={l.href}
                                            className={classes.megaLink}
                                            onClick={() => setOpenedDesktopMenu(null)}
                                          >
                                            <span className={classes.megaLabel}>
                                              {l.label}
                                            </span>
                                            {l.description && (
                                              <span className={classes.megaDesc}>
                                                {l.description}
                                              </span>
                                            )}
                                          </Link>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </Popover.Dropdown>
                        </Popover>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              <div className={classes.actions}>
                <div className={classes.rightLinks}>
                  {isLoggedIn ? (
                    <>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className={classes.rightLinkButton}
                      >
                        {dictionary?.header?.auth?.logoutLabel ?? data.auth.logoutLabel}
                      </button>
                      <Link href={data.auth.dashboardHref} className={classes.rightLink}>
                        {dictionary?.header?.auth?.dashboardLabel ??
                          data.auth.dashboardLabel}
                      </Link>
                    </>
                  ) : (
                    <Link href={data.auth.loginHref} className={classes.rightLink}>
                      {dictionary?.header?.auth?.loginLabel ?? data.auth.loginLabel}
                    </Link>
                  )}
                </div>

                {data.cta && (
                  <Link href={data.cta.href} className={classes.cta}>
                    {data.cta.label}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {hasImportant && (
          <div
            className={`${classes.importantBanner} ${showBanner ? classes.importantBannerVisible : ''
              }`}
            role="status"
            aria-live="polite"
            style={bannerStyle}
          >
            <div className={classes.importantBannerInner}>
              <span className={classes.importantDot} aria-hidden="true" />
              <p className={classes.importantText}>{importantText}</p>
            </div>
          </div>
        )}
      </header>

      <Drawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        position="right"
        size="min(92vw, 380px)"
        withCloseButton={false}
        overlayProps={{ opacity: 0.35, blur: 10 }}
        transitionProps={{ duration: 420, timingFunction: 'cubic-bezier(.2,.9,.2,1)' }}
        withinPortal
      >
        <div className={classes.drawer}>
          <div className={classes.drawerTop}>
            <Link
              href="/"
              className={classes.drawerBrand}
              onClick={() => setDrawerOpen(false)}
            >
              <Image
                src={data.logoSrc}
                alt={dictionary.compagnyName}
                width={94}
                height={94}
                unoptimized
                sizes="94px"
                quality={45}
              />
            </Link>

            <button
              type="button"
              className={classes.drawerClose}
              onClick={() => setDrawerOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <div className={classes.drawerContent}>
            {data.nav.map((item) => {
              const hasDropdown = item.columns && item.columns.length > 0;

              if (!hasDropdown) {
                return (
                  <Link
                    key={item.label}
                    href={item.href ?? '#'}
                    className={classes.drawerLink}
                    onClick={() => setDrawerOpen(false)}
                  >
                    {item.label}
                  </Link>
                );
              }

              return (
                <details key={item.label} className={classes.drawerSection}>
                  <summary className={classes.drawerSummary}>
                    <span>{item.label}</span>
                    <span className={classes.drawerChevron} aria-hidden="true">
                      ▾
                    </span>
                  </summary>

                  <div className={classes.drawerColumns}>
                    {item.columns!.map((col) => (
                      <div key={col.title}>
                        <p className={classes.drawerColTitle}>{col.title}</p>
                        {col.links.map((l) => (
                          <Link
                            key={l.href}
                            href={l.href}
                            className={classes.drawerSublink}
                            onClick={() => setDrawerOpen(false)}
                          >
                            {l.label}
                          </Link>
                        ))}
                      </div>
                    ))}
                  </div>
                </details>
              );
            })}

            <div className={classes.drawerActions}>
              <div className={classes.drawerLangRow}>
                <span className={classes.drawerSmallLabel}>Language</span>
                <div className={classes.drawerLangButtons}>
                  {(['fr', 'en'] as Locale[]).map((loc) => {
                    const meta = supportedLanguage[loc] ?? DEFAULT_LANG_META[loc];
                    const active = loc === currentLocale;

                    return (
                      <button
                        key={loc}
                        type="button"
                        className={`${classes.drawerLangBtn} ${active ? classes.drawerLangBtnActive : ''
                          }`}
                        onClick={() => changeLanguage(loc)}
                      >
                        <img
                          src={meta.img}
                          alt={meta.label}
                          width={18}
                          height={18}
                          style={{ borderRadius: 999 }}
                        />
                        <span>{meta.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {isLoggedIn ? (
                <>
                  <Link
                    href={data.auth.dashboardHref}
                    className={classes.drawerPrimary}
                    onClick={() => setDrawerOpen(false)}
                  >
                    {dictionary?.header?.auth?.dashboardLabel ??
                      data.auth.dashboardLabel}
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      setDrawerOpen(false);
                      handleLogout();
                    }}
                    className={classes.drawerSecondary}
                  >
                    {dictionary?.header?.auth?.logoutLabel ?? data.auth.logoutLabel}
                  </button>
                </>
              ) : (
                <Link
                  href={data.auth.loginHref}
                  className={classes.drawerPrimary}
                  onClick={() => setDrawerOpen(false)}
                >
                  {dictionary?.header?.auth?.loginLabel ?? data.auth.loginLabel}
                </Link>
              )}

              {data.cta && (
                <Link
                  href={data.cta.href}
                  className={classes.drawerCta}
                  onClick={() => setDrawerOpen(false)}
                >
                  {data.cta.label}
                </Link>
              )}
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
}